import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { Header } from '../components/layout/Header';
import { Navigation } from '../components/layout/Navigation';
import { MobileStickyBar } from '../components/layout/MobileStickyBar';
import { Footer } from '../components/layout/Footer';
import { useScreenInit } from '../useScreenInit';
import { CategorySeo, useCategorySeo } from '../hooks/useCategorySeo';
import { usePageTitle } from '../hooks/usePageTitle';
import { CategoryData, Product } from '../data/residentialCategories';
import { ListingToolbar, ListingPagination, SortKey, PageSize, SORT_OPTIONS, PAGE_SIZE_OPTIONS } from '../components/category/ListingControls';
import { Phone, Package, ExternalLink, ChevronRight, Info } from 'lucide-react';
function parsePrice(price: string): number {
  const n = parseFloat((price || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}
function sortProducts(products: Product[], sort: SortKey): Product[] {
  const list = [...products];
  switch (sort) {
    case 'price-asc':
    case 'price-desc':
      {
        const dir = sort === 'price-asc' ? 1 : -1;
        return list.sort((a, b) => {
          const pa = parsePrice(a.price);
          const pb = parsePrice(b.price);
          const aMissing = Number.isNaN(pa);
          const bMissing = Number.isNaN(pb);
          // Priced items always sort before quote-only / missing-price items.
          if (aMissing && bMissing) return 0;
          if (aMissing) return 1;
          if (bMissing) return -1;
          return (pa - pb) * dir;
        });
      }
    case 'name-asc':
      return list.sort((a, b) => a.title.localeCompare(b.title));
    case 'name-desc':
      return list.sort((a, b) => b.title.localeCompare(a.title));
    case 'recommended':
    default:
      return list;
  }
}
function ProductCard({
  product
}: {
  product: Product;
}) {
  const [imageError, setImageError] = useState(false);
  return <div className="group bg-white rounded-xl border border-brand-border overflow-hidden hover:shadow-md hover:border-brand-maroon/50 transition-all flex flex-col h-full" data-id="element-2151" data-mp="258mcz1xzyu2m">
      <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="block relative bg-white aspect-square p-6 border-b border-brand-border/50 flex items-center justify-center" aria-label={`View ${product.title}`} data-id="element-2152" data-mp="20c5dxcp9evar">
        {!imageError ? <img src={product.imageUrl} alt={product.title} onError={() => setImageError(true)} className="w-full h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500" data-id="element-2153" data-mp="1hjb8pahfuhjq" /> : <div className="w-full h-full flex flex-col items-center justify-center text-brand-charcoal/20" data-id="element-2154" data-mp="18kpvzvyndwqv">
            <Package size={48} strokeWidth={1} data-id="element-2155" data-mp="36vwvgsssg8dk" />
            <span className="mt-2 text-xs font-medium tracking-wide" data-id="element-2156" data-mp="32xphg5ppll62">
              Image Unavailable
            </span>
          </div>}
      </a>

      <div className="p-5 flex flex-col flex-grow" data-id="element-2157" data-mp="tnztpwj9hxpj">
        <div className="text-xs text-gray-500 mb-1 font-mono uppercase tracking-wider" data-id="element-2158" data-mp="tmoorte2yjwu">
          {product.sku}
        </div>
        <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-charcoal hover:text-brand-maroon transition-colors mb-3 line-clamp-2 leading-snug" data-id="element-2159" data-mp="ygsyulin7825">
          {product.title}
        </a>
        <div className="text-lg font-bold text-brand-charcoal mb-5 mt-auto" data-id="element-2160" data-mp="apnhu30yu9q9">
          {product.price}
        </div>

        <a href={product.productUrl} target="_blank" rel="noopener noreferrer" className="block w-full bg-brand-background border border-brand-border hover:border-brand-maroon hover:text-brand-maroon text-brand-charcoal text-center py-2.5 rounded-md font-semibold transition-colors text-sm flex items-center justify-center gap-2" data-id="element-2161" data-mp="estm554ipqu3">
          View Details <ExternalLink size={14} data-id="element-2162" data-mp="1to1ub1dim8zg" />
        </a>
      </div>
    </div>;
}
export function ResidentialCategory({
  category,
  context = 'Mailboxes',
  fallbackDescription,
  seoOverride
}: {
  category: CategoryData;
  context?: string;
  fallbackDescription?: string;
  seoOverride?: CategorySeo;
}) {
  useScreenInit();
  // Baseline title for every category page; a route-specific SEO entry (or a
  // local override) replaces it when one is available.
  usePageTitle(`${category.label} | MidAtlantic Mailboxes`);
  const seo = useCategorySeo(category.route, seoOverride);
  const [searchParams, setSearchParams] = useSearchParams();
  const products = category.products;
  const hasProducts = products.length > 0;
  const sortParam = searchParams.get('sort') as SortKey | null;
  const sort: SortKey = sortParam && SORT_OPTIONS.some(o => o.value === sortParam) ? sortParam : 'recommended';
  const sizeParam = searchParams.get('pageSize');
  const pageSize: PageSize = sizeParam === 'all' ? 'all' : sizeParam && PAGE_SIZE_OPTIONS.some(o => String(o.value) === sizeParam) ? Number(sizeParam) as PageSize : 16;
  const sorted = useMemo(() => sortProducts(products, sort), [products, sort]);
  const total = sorted.length;
  const perPage = pageSize === 'all' ? total || 1 : pageSize;
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(total / perPage));
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Math.min(Math.max(1, Number.isFinite(pageParam) ? pageParam : 1), totalPages);
  const startIdx = pageSize === 'all' ? 0 : (page - 1) * perPage;
  const visible = pageSize === 'all' ? sorted : sorted.slice(startIdx, startIdx + perPage);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = pageSize === 'all' ? total : Math.min(startIdx + perPage, total);
  const resultLabel = pageSize === 'all' ? `Showing all ${total} ${total === 1 ? 'product' : 'products'}` : `Showing ${showingFrom}-${showingTo} of ${total} products`;
  const paginationActive = pageSize !== 'all' && totalPages > 1;
  const updateParams = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => v === null ? sp.delete(k) : sp.set(k, v));
    setSearchParams(sp, {
      replace: true
    });
  };
  const onSortChange = (s: SortKey) => updateParams({
    sort: s === 'recommended' ? null : s,
    page: null
  });
  const onPageSizeChange = (p: PageSize) => updateParams({
    pageSize: p === 16 ? null : String(p),
    page: null
  });
  const onPageChange = (p: number) => updateParams({
    page: p <= 1 ? null : String(p)
  });
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-2163" data-mp="3djcuxg1nb50c">
      <TopBar data-id="element-2164" data-mp="38hnl23q5sxdu" />
      <Header data-id="element-2165" data-mp="38hw0o3do2gsq" />
      <Navigation data-id="element-2166" data-mp="38hdv2g546q85" />

      <main className="flex-grow" data-id="element-2167" data-mp="38l1ntd2qj8lt">
        {/* Category Hero */}
        <section className="bg-white border-b border-brand-border py-8 lg:py-10" data-id="element-2168" data-mp="1o27mq3rlau63">
          <div className="max-w-7xl mx-auto px-4" data-id="element-2169" data-mp="1t0snc4ar7fv2">
            {/* Breadcrumbs */}
            <nav className="flex text-sm text-gray-500 mb-4 overflow-x-auto hide-scrollbar whitespace-nowrap pb-2" data-id="element-2170" data-mp="1x12sjeqq228v">
              <ol className="flex items-center space-x-2" data-id="element-2171" data-mp="21sum7y56co38">
                <li data-id="element-2172" data-mp="26re9c1vi85x6">
                  <a href="/Home.html" className="hover:text-brand-maroon transition-colors" data-id="element-2173" data-mp="26rxesj3yx1ub">
                    Home
                  </a>
                </li>
                <li data-id="element-2174" data-mp="2gqmy8gbiiuco">
                  <ChevronRight size={14} className="text-gray-400" data-id="element-2175" data-mp="2gq3k7aozgq3x" />
                </li>
                <li data-id="element-2176" data-mp="2kqdomix4n8go">
                  <span className="text-gray-400" data-id="element-2177" data-mp="2kokqjs0kxwka">{context}</span>
                </li>
                <li data-id="element-2178" data-mp="md3xyyq3kxno">
                  <ChevronRight size={14} className="text-gray-400" data-id="element-2179" data-mp="qcw83fv53bv5" />
                </li>
                <li className="text-brand-charcoal font-medium" data-id="element-2180" data-mp="qepfixdku047">
                  {category.label}
                </li>
              </ol>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6" data-id="element-2181" data-mp="2grhamj5dcby">
              <div className="max-w-3xl" data-id="element-2182" data-mp="g7wb37zu3aed">
                <h1 className="text-3xl lg:text-4xl xl:text-5xl font-serif font-bold text-brand-charcoal mb-4" data-id="element-2183" data-mp="gb22itvxqod9">
                  {category.label}
                </h1>
                {seo?.description || fallbackDescription ? <p className="text-lg text-brand-muted leading-relaxed" data-id="element-2184" data-mp="3fom6kdbftwea">
                    {seo?.description || fallbackDescription}
                  </p> : <div className="space-y-2.5 max-w-2xl" aria-hidden="true" role="presentation" data-id="element-2185" data-mp="3oor1hnzg32bk">
                    <div className="h-4 rounded bg-brand-border/60 w-full" data-id="element-2186" data-mp="18zpq4w45pptz" />
                    <div className="h-4 rounded bg-brand-border/60 w-11/12" data-id="element-2187" data-mp="13xqobqo2f0z5" />
                    <div className="h-4 rounded bg-brand-border/60 w-3/5" data-id="element-2188" data-mp="1cfaotxsqictk" />
                  </div>}
              </div>

              <div className="shrink-0 bg-brand-goldWash border border-brand-gold/30 rounded-xl p-5 flex items-center gap-4" data-id="element-2189" data-mp="1gigw98rl2a1m">
                <div className="w-10 h-10 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm shrink-0" data-id="element-2190" data-mp="17gfzed09hq0h">
                  <Phone size={20} data-id="element-2191" data-mp="1v738h1lwylom" />
                </div>
                <div data-id="element-2192" data-mp="1v8eel82u05iw">
                  <p className="text-sm font-bold text-brand-charcoal mb-0.5" data-id="element-2193" data-mp="205mwd7k2i7jm">
                    Need help choosing?
                  </p>
                  <a href="tel:301-620-7585" className="text-brand-maroon font-semibold hover:underline" data-id="element-2194" data-mp="1lfpdn74wqa1t">
                    Call 301-620-7585
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product Grid */}
        <section className="py-8 lg:py-10" data-id="element-2195" data-mp="1gqobbj3m5yb7">
          <div className="max-w-7xl mx-auto px-4" data-id="element-2196" data-mp="1grpyun1tgd9t">
            {hasProducts ? <>
                {/* Listing controls toolbar */}
                <div className="mb-6" data-id="element-2197" data-mp="17pqebkrler24">
                  <ListingToolbar sort={sort} onSortChange={onSortChange} pageSize={pageSize} onPageSizeChange={onPageSizeChange} resultLabel={resultLabel} data-id="element-2198" data-mp="17oowmxg2n6bf" />
                </div>

                {paginationActive && <div className="mb-6" data-id="element-2199" data-mp="12sq7h7mx5k9y">
                    <ListingPagination page={page} totalPages={totalPages} onPageChange={onPageChange} data-id="element-2200" data-mp="p01qebm5saw9" />
                  </div>}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-2201" data-mp="f1kur1c9z83v">
                  {visible.map((product, idx) => <ProductCard key={`${product.sku}-${startIdx}-${idx}`} product={product} data-id="element-2202" data-mp="du99zzgyo0z" data-mpi={idx} />)}
                </div>

                {paginationActive && <div className="mt-8" data-id="element-2203" data-mp="ae4mvlpf1xgo">
                    <ListingPagination page={page} totalPages={totalPages} onPageChange={onPageChange} data-id="element-2204" data-mp="5dgqekif4of2" />
                  </div>}
              </> : <div className="bg-white border border-brand-border rounded-xl p-8 lg:p-12 text-center max-w-2xl mx-auto" data-id="element-2205" data-mp="edvc3kzao5j3">
                <div className="w-14 h-14 rounded-full bg-brand-goldWash border border-brand-gold/30 text-brand-maroon flex items-center justify-center mx-auto mb-5" data-id="element-2206" data-mp="t4vtifi0r9rd">
                  <Package size={26} strokeWidth={1.5} data-id="element-2207" data-mp="12492z8657yc3" />
                </div>
                <h2 className="font-serif text-xl font-semibold text-brand-charcoal mb-2" data-id="element-2208" data-mp="y35ooq3quacz">
                  Browse {category.label} with our team
                </h2>
                <p className="text-brand-muted leading-relaxed mb-6" data-id="element-2209" data-mp="1a45o72xqv50e">
                  We carry a full selection in this category. Tell us what
                  you’re looking for and we’ll help you find the right option.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-2210" data-mp="1jv0a2672w2lb">
                  <a href="tel:301-620-7585" className="inline-flex items-center justify-center gap-2 bg-brand-maroon hover:bg-brand-maroonDark text-white px-6 py-2.5 rounded-md font-semibold transition-colors" data-id="element-2211" data-mp="1nusd63dpdgja">
                    <Phone size={16} data-id="element-2212" data-mp="22st2ufnzmw4z" /> Call 301-620-7585
                  </a>
                  <a href="/ContactUs.html" className="inline-flex items-center justify-center gap-2 bg-brand-background border border-brand-border hover:border-brand-maroon hover:text-brand-maroon text-brand-charcoal px-6 py-2.5 rounded-md font-semibold transition-colors" data-id="element-2213" data-mp="22qqes16ebd2w">
                    Ask for help
                  </a>
                </div>
              </div>}
          </div>
        </section>

        {/* Buyer guidance / SEO supporting copy */}
        {seo?.extraText && <section className="pb-12 lg:pb-16" data-id="element-2214" data-mp="27z7yulw7w9w9">
            <div className="max-w-7xl mx-auto px-4" data-id="element-2215" data-mp="34o78o5cg9jqk">
              <div className="bg-white border border-brand-border rounded-xl p-6 lg:p-8 flex flex-col sm:flex-row gap-5" data-id="element-2216" data-mp="34mw3c13cw3xc">
                <div className="hidden sm:flex w-11 h-11 rounded-xl bg-brand-goldWash border border-brand-gold/30 text-brand-maroon items-center justify-center shrink-0" data-id="element-2217" data-mp="30khmtnn5tg98">
                  <Info size={22} strokeWidth={1.75} data-id="element-2218" data-mp="2qnmrfdm6nvz6" />
                </div>
                <div className="max-w-3xl" data-id="element-2219" data-mp="1k8m3nv7bak3o">
                  <h2 className="font-serif text-xl font-semibold text-brand-charcoal mb-3" data-id="element-2220" data-mp="1k9nd5v8oiyja">
                    About {category.label}
                  </h2>
                  <p className="text-brand-muted leading-relaxed whitespace-pre-line" data-id="element-2221" data-mp="335p8jaqkzba5">
                    {seo.extraText}
                  </p>
                </div>
              </div>
            </div>
          </section>}
      </main>

      <Footer data-id="element-2222" data-mp="2u364woez0umu" />
      <MobileStickyBar data-id="element-2223" data-mp="2u3fyelz85jxa" />
    </div>;
}